import { useMemo, useCallback, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import qs from 'qs';
import classNames from 'classnames/bind';
import AdyenCheckout from '@adyen/adyen-web';
import '@adyen/adyen-web/dist/adyen.css';
import paymentService from 'features/payment/services';
import { getPaymentMethodsConfig } from 'common/helpers/payments';
import styles from './PaymentContainer.module.scss';
import { isNullOrWhiteSpace } from 'common/helpers/string';
import { useAppSelector } from 'app/hooks';
import { selectCurrentMarket } from 'features/core/selectors';
import { Market } from 'common/consts/markets';

type WrapperProps = {
  Model: any;
  PageData: any;
  IsDirectPayment?: boolean;
  IsConfirmPaymentPlan?: boolean;
  showPayButton?: boolean;
};

interface CheckoutProps {
  config: any;
  Model: any;
  PageData: any;
  IsDirectPayment?: boolean;
}

const PaymentWrapper = ({
  Model,
  PageData,
  IsDirectPayment,
  IsConfirmPaymentPlan = false,
  showPayButton = true,
}: WrapperProps) => {
  const currentMarket = useAppSelector(selectCurrentMarket);
  const paymentMethodsConfiguration = useMemo(
    () => getPaymentMethodsConfig(Model,currentMarket),
    [Model],
  );
  const REGEXP_EMAIL =
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  var translations = {
    en_US: {
      'payment.processing': PageData?.CustomizedPaymentProcessingMessage,
    },
    es_ES: {
      'payment.processing': PageData?.CustomizedPaymentProcessingMessage,
    },
  };

  const config = useMemo(() => {
    if (Model) {
      return {
        amount: {
          value: Model?.AdyenForm?.PaymentAmount?.value,
          currency: Model?.AdyenForm?.PaymentAmount?.currency,
        },
        paymentMethodsResponse: {
          paymentMethods: Model?.AdyenForm?.PaymentMethods,
          storedPaymentMethods: Model?.AdyenForm?.StoredPaymentMethods,
        },
        clientKey: Model?.AdyenForm?.ClientKey,
        locale: Model?.AdyenForm?.Locale,
        environment: Model?.AdyenForm?.SdkMode,
        setStatusAutomatically: true,
        showPayButton: showPayButton,

        onSubmit: function (state: any, dropin: any) {
          dropin.setStatus('loading');
          makePayment(state.data, dropin);
        },
        onError: function (error: any) {
          console.error(error);
        },
        onAdditionalDetails: function (state: any, dropin: any) {
          handleSubmission(state, dropin);
        },
        paymentMethodsConfiguration: paymentMethodsConfiguration,
        translations: translations,
      };
    }
  }, [Model]);

  const adyenDropinCheckout = async (config: any) => {
    try {
      return await AdyenCheckout(config).then((response) => {
        return response;
      });
    } catch (err) {
      console.error(err);
    }
  };

  const Checkout = ({
    config,
    Model,
    PageData,
    IsDirectPayment,
  }: CheckoutProps) => {
    const history = useHistory();
    const paymentContainer = useRef(null);
    let adyenCheckout: any;
    const checkout = adyenDropinCheckout(config) as any;
    /* Return pending result as checkout is async */
    checkout.then(function (result: any) {
      adyenCheckout = result;
    });
    let showConfirmMessage: boolean = false;
    showConfirmMessage = !isNullOrWhiteSpace(Model?.AdyenForm?.ConfirmMessage);
    let confirmMessage = Model?.AdyenForm?.ConfirmMessage;

    function changeConfirmMessage(e: any, component: any) {
      let paybtns: any = component.elementRef._node.getElementsByClassName(
        'adyen-checkout__button adyen-checkout__button--pay',
      );
      let lenPayBtns = paybtns.length;
      if (lenPayBtns >= 1) {
        if (e.srcElement.checked === true) {
          paybtns[lenPayBtns - 1].disabled = false;
        } else {
          paybtns[lenPayBtns - 1].disabled = true;
        }
      }
    }
    function changeEmailMessage(e: any, component: any) {
      let paybtns: any = component.elementRef._node.getElementsByClassName(
        'adyen-checkout__button adyen-checkout__button--pay',
      );
      let lenPayBtns = paybtns.length;
      if (lenPayBtns >= 1) {
        if (e.target.value === '') {
          paybtns[lenPayBtns - 1].disabled = true;
        } else {
          paybtns[lenPayBtns - 1].disabled = false;
        }
      }
    }

    function createMarkup(component: any) {
      let label = document.createElement('label');
      label.classList.add('Checkbox');
      let check = document.createElement('input');
      check.type = 'checkbox';
      check.id = 'ChkConfirmMessage';
      check.onchange = (val: any) => changeConfirmMessage(val, component);
      check.classList.add('Checkbox-adyen__adyen');
      check.name = 'name';
      let span1 = document.createElement('span');
      span1.classList.add('Checkbox__label');
      span1.textContent = confirmMessage;
      label.append(check);
      label.append(span1);
      return label;
    }
    function createMarkupSecurePayment(component: any) {
      let container = document.createElement('div');
      container.classList.add('AdyenConfirmationBox');

      let label = document.createElement('label');
      let headerText = document.createElement('H2');
      headerText.classList.add('HeaderText');
      headerText.append(PageData?.ConfirmDetailsHeading);

      let bodyText = document.createElement('p');
      bodyText.classList.add('ParagraphText');

      bodyText.innerHTML = PageData?.ConfirmDetailsBodyFreeText;

      label.classList.add('Checkbox');
      let check = document.createElement('input');
      check.type = 'checkbox';
      check.id = 'ChkConfirmMessage';
      check.onchange = (val: any) => changeConfirmMessage(val, component);
      check.classList.add('Checkbox-adyen__adyen');
      check.name = 'name';
      let span1 = document.createElement('span');
      span1.classList.add('Checkbox__label');
      span1.textContent = PageData?.IAgreeLabel;

      label.append(check);
      label.append(span1);
      container.append(headerText);
      container.append(bodyText);
      container.append(label);

      return container;
    }
    useEffect(() => {
      if (!paymentContainer) {
        return;
      }

      if (paymentContainer.current && adyenCheckout) {
        adyenCheckout
          .create('dropin', {
            openFirstPaymentMethod: Model?.AdyenForm?.OpenFirstPaymentMethod,
            showRemovePaymentMethodButton:
              Model?.AdyenForm?.ShowRemovePaymentMethodButton,
            openFirstStoredPaymentMethod:
              Model?.AdyenForm?.OpenFirstStoredPaymentMethod,
            showStoredPaymentMethods:
              Model?.AdyenForm?.ShowStoredPaymentMethods,

            onSelect: (component: any) => {
              if (
                (component?.props?.type == 'sepadirectdebit' ||
                  component?.props?.type == 'ideal' ||
                  component?.props?.type == 'bcmc') &&
                showConfirmMessage === true &&
                !IsConfirmPaymentPlan &&
                component?.props?.storedPaymentMethodId == undefined
              ) {
                setTimeout(() => {
                  const existingdivs =
                    component.elementRef._node.getElementsByTagName('div');
                  let isConfirmdivexisting: boolean = false;
                  for (const child of existingdivs) {
                    let confirmdiv = child.id;
                    if (confirmdiv == 'div_confirmbox') {
                      isConfirmdivexisting = true;
                    }
                  }

                  if (isConfirmdivexisting == false) {
                    const check = document.createElement('div');
                    check.id = 'div_confirmbox';
                    check.classList.add('adyen-confirm-message', 'div-element');
                    check.appendChild(createMarkup(component));
                    let paybtns: any =
                      component.elementRef._node.getElementsByClassName(
                        'adyen-checkout__button adyen-checkout__button--pay',
                      );
                    let lenPayBtns = paybtns.length;
                    if (lenPayBtns >= 1) {
                      paybtns[lenPayBtns - 1].before(check);
                      paybtns[lenPayBtns - 1].disabled = true;
                    }
                  }
                }, 500);
              }
              if (component?.props?.type == 'onlineBanking_PL') {
                setTimeout(() => {
                  const inputTextBox =
                    document.getElementById('textEmailAddress');
                  if (inputTextBox == undefined || inputTextBox == null) {
                    const inputdiv = document.createElement('div');
                    const inputElement = document.createElement('input');
                    inputdiv.classList.add('email_address');
                    inputElement.id = 'textEmailAddress';
                    inputElement.setAttribute('type', 'text');
                    inputElement.setAttribute('value', Model?.AdyenForm?.ShopperEmail);
                    inputElement.setAttribute('autocomplete', 'off');
                    inputElement.setAttribute(
                      'placeholder',
                      PageData?.EmailAddressPlaceholderText,
                    );
                    
                    const para = document.createElement('p');
                    para.innerHTML = PageData?.EmailAddressMessageText;
                    inputdiv.append(inputElement);
                    inputdiv.append(para);

                    let paybtns: any =
                      component.elementRef._node.getElementsByClassName(
                        'adyen-checkout__button adyen-checkout__button--pay',
                      );
                    let lenPayBtns = paybtns.length;
                    if (lenPayBtns >= 1) {
                      paybtns[lenPayBtns - 1].before(inputdiv);                      
                      paybtns[lenPayBtns - 1].disabled = true;
                      if(Model?.AdyenForm?.ShopperEmail !== '')
                      {
                      inputElement.setAttribute('value', Model?.AdyenForm?.ShopperEmail);
                      paybtns[lenPayBtns - 1].disabled = false;
                      }
                    }

                    inputElement.addEventListener('input', () => {
                      if (lenPayBtns >= 1) {                        
                          if (REGEXP_EMAIL.test(inputElement.value)) {
                            paybtns[lenPayBtns - 1].disabled = false;                            
                          }
                          else {                         
                            paybtns[lenPayBtns - 1].disabled = true;                          
                          }                                               
                      }
                    });
                  }
                }, 800);
              }
              if (
                (component?.props?.type == 'card' ||
                  component?.props?.type == 'sepadirectdebit' ||
                  component?.props?.type == 'ideal' ||
                  component?.props?.type == 'bcmc') &&
                IsConfirmPaymentPlan
              ) {
                setTimeout(() => {
                  const securePay = document.createElement('div');
                  const check = document.createElement('div');
                  check.classList.add('adyen-confirmplan-container');
                  if (
                    component?.props?.type == 'ideal' ||
                    component?.props?.type == 'bcmc'
                  ) {
                    check.appendChild(createMarkupSecurePayment(component));
                  }
                  const securePayDiv = document.createElement('div');
                  securePayDiv.classList.add('logowrapper');
                  securePayDiv.classList.add('logowrapper_confirm');
                  let span2 = document.createElement('span');
                  span2.classList.add('logotext');
                  span2.textContent = 'Secure Payment';
                  securePayDiv.appendChild(span2);
                  securePay.append(check);
                  securePay.append(securePayDiv);

                  let btn: any =
                    component.elementRef._node.getElementsByTagName(
                      'button',
                    )[1];
                  btn.before(securePay);
                  if (
                    component?.props?.type == 'ideal' ||
                    component?.props?.type == 'bcmc'
                  ) {
                    btn.disabled = true;
                  }
                }, 500);
              }
            },
            onDisableStoredPaymentMethod: (
              storedPaymentMethodId: any,
              resolve: any,
              reject: any,
            ) => {
              paymentService
                .disableStoredPaymentMethod(
                  Model?.AdyenForm?.DisableStoredMethodUrl,
                  {
                    storedPaymentId: storedPaymentMethodId,
                  },
                )
                .then((response) => resolve(response))
                .catch((error) => reject(error));
            },
          })

          .mount(paymentContainer.current);
      }
    }, [config, Model]);

    return (
      <div className={styles.wrapper}>
        <div ref={paymentContainer} />
        {IsDirectPayment && (
          <div className={styles.ctaWrapper}>
            <button
              onClick={history.goBack}
              className={classNames(
                'Button Button--gray Highlight-button u-marginBxsm',
                styles.cancelBtn,
              )}
            >
              {PageData?.CancelButton}
            </button>
          </div>
        )}
      </div>
    );
  };

  const makePayment = useCallback(
    async (state, dropin) => {
      const txtShopperEmail = document.getElementById('textEmailAddress');
      let shopperEmailValue =
        txtShopperEmail != undefined || txtShopperEmail != null
          ? (document.getElementById('textEmailAddress') as HTMLInputElement)
              .value
          : '';
      const dataWithAmount = {
        ...state,
        paymentAmount: config?.amount,
        shopperEmail: shopperEmailValue,
      };
      try {
        const response = await paymentService.makePayment(
          Model?.AdyenForm?.MakePaymentUrl,
          qs.stringify(dataWithAmount),
        );
        if (response.data?.action) {
          Object.keys(response.data?.action).forEach(key => {
            if (response.data?.action[key] === null) {
              delete response.data?.action[key];
            }
          });
          dropin.handleAction(response.data.action);
        } else {
          showFinalResults(response.data, dropin);
        }
      } catch (err) {
        dropin.setStatus('error', { message: err });
        console.error(err);
      }
    },
    [Model],
  );

  const handleSubmission = useCallback(
    async (state, dropin) => {
      try {
        const data = { ...state.data };
        const response = await paymentService.postPaymentValues(
          Model?.AdyenForm?.MakePaymentDetailsUrl,
          qs.stringify(data),
        );
        if (response.data?.action) {
          dropin.handleAction(response.data?.action);
        } else {
          showFinalResults(response?.data, dropin);
        }
      } catch (err) {
        console.error(err);
        dropin.setStatus('error', { message: err });
      }
    },
    [Model],
  );

  const showFinalResults = useCallback(
    async (response: any, dropin: any) => {
      switch (response.resultCode) {
        case 'Authorised':
        case 'Pending':
        case 'PresentToShopper':
        case 'Received':
          dropin.setStatus('success');
          break;
        case 'Cancelled':
        case 'Refused':
          dropin.setStatus('error');
          break;
        case 'Error':
          dropin.setStatus('error');
          break;
      }
      const data = {
        resultCode: response.resultCode,
        refusalReason: response.refusalReson,
        paymentAmount: Model?.AdyenForm?.PaymentAmount?.value,
        paymentMethod: dropin?.data?.paymentMethod?.type,
        payFullBalance: Model?.AdyenForm?.IsPayFullBalance,
        payerReviewStatus: Model?.AdyenForm?.PayerReviewSelectedStatus,
        merchantReference: response.merchantReference,
        fromOffer: Model?.AdyenForm?.IsFromOffer,
        pspReference: response.pspReference,
        withCallCenterPlan: Model?.AdyenForm?.WithCallCenterPlan,
        enableStoreDetails: Model?.AdyenForm?.EnableStoreDetails,
      };
      const postPaymentResponse = await paymentService.postPaymentValues(
        Model?.AdyenForm?.PostPaymentValuesUrl,
        qs.stringify(data),
      );
      if (
        postPaymentResponse?.data &&
        postPaymentResponse?.data.status === 'redirect' &&
        postPaymentResponse.data?.data
      ) {
        // Redirect to web
        window.location.href = postPaymentResponse.data?.data;
      }
    },
    [Model],
  );

  return (
    <div>
      <Checkout
        config={config}
        Model={Model}
        PageData={PageData}
        IsDirectPayment={IsDirectPayment}
      />
    </div>
  );
};

export default PaymentWrapper;
